import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, Layers, Microscope, Repeat, Activity } from "lucide-react";
import PageShell from "@/components/layout/PageShell";
import SubHero from "@/components/layout/SubHero";
import NextStep from "@/components/layout/NextStep";
import { Reveal, SectionOverline } from "@/components/landing/Shared";

const SECTIONS = [
  {
    key: "why",
    icon: Compass,
    number: "01",
    title: "Why Adaptive Physiology?",
    desc:
      "The gap in behavior change — and why physiology is the missing input in most existing models.",
    to: "/methodology/why",
  },
  {
    key: "framework",
    icon: Layers,
    number: "02",
    title: "The Framework",
    desc:
      "Four pillars: Physiological Regulation · Behavioral Activation · Recreation & Engagement · Adaptive Performance.",
    to: "/methodology/framework",
  },
  {
    key: "science",
    icon: Microscope,
    number: "03",
    title: "The Science",
    desc:
      "Research and physiological foundations — HRV, autonomic tone, BDNF, cortisol regulation, neuroplasticity.",
    to: "/methodology/science",
  },
  {
    key: "how",
    icon: Repeat,
    number: "04",
    title: "How It Works",
    desc:
      "The closed-loop protocol: Assess → Prescribe → Reinforce → Retrain → Reassess.",
    to: "/methodology/how-it-works",
  },
  {
    key: "outcomes",
    icon: Activity,
    number: "05",
    title: "Outcomes",
    desc:
      "What the methodology measurably shifts — in physiology, behavior, and long-term capacity.",
    to: "/methodology/outcomes",
  },
];

const MethodologyOverview = () => {
  return (
    <PageShell>
      <SubHero
        testId="methodology-hero"
        breadcrumb={[{ label: "Home", to: "/" }, { label: "Methodology" }]}
        overline="The Methodology"
        headline="Adaptive Physiology™ is a closed-loop methodology,"
        headlineAccent="not a program."
        lead="It applies exercise physiology, behavioral conditioning, and environmental adaptation to retrain the physiological state that drives behavior. Every input is measured. Every response informs the next prescription. The loop closes — and the physiology adapts."
      />

      <section
        data-testid="methodology-principle-section"
        className="relative py-24 sm:py-32"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 lg:col-span-5">
              <Reveal>
                <SectionOverline
                  number="00"
                  label="Foundational Premise"
                  testId="methodology-premise-overline"
                />
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="mt-8 font-display forr-text-ink text-3xl sm:text-4xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.08]">
                  Behavior is an output of a physiological state.
                </h2>
              </Reveal>
            </div>
            <div className="col-span-12 lg:col-span-7 lg:pl-8">
              <Reveal delay={0.1}>
                <p className="text-lg font-light forr-text-soft leading-relaxed">
                  Motivation, emotional regulation, stress tolerance, focus,
                  recovery, and engagement are not independent traits. They are
                  expressions of an underlying physiological system.
                </p>
                <p className="mt-6 text-lg font-light forr-text-soft leading-relaxed">
                  Change the state, and the behavior follows. Adaptive Physiology
                  is the methodology for doing that systematically —
                  through inputs the nervous system is designed to respond to.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section
        data-testid="methodology-sections-grid"
        className="relative py-24 sm:py-32 border-t border"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <SectionOverline
              number="→"
              label="Explore The Methodology"
              testId="methodology-explore-overline"
            />
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-8 max-w-3xl font-display forr-text-ink text-3xl sm:text-4xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.08]">
              Five sections. One coherent methodology.
            </h2>
          </Reveal>

          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SECTIONS.map((s, i) => (
              <Reveal delay={i * 0.06} key={s.key}>
                <Link
                  to={s.to}
                  data-testid={`methodology-card-${s.key}`}
                  className="forr-card block rounded-[24px] p-8 h-full group"
                >
                  <div className="flex items-center justify-between mb-8">
                    <div className="w-11 h-11 rounded-2xl bg-teal-tint border border-teal-soft flex items-center justify-center forr-text-teal-deep">
                      <s.icon size={20} strokeWidth={1.5} />
                    </div>
                    <span className="text-[11px] uppercase tracking-[0.24em] forr-text-mute">
                      {s.number}
                    </span>
                  </div>
                  <h3 className="font-display forr-text-ink text-xl font-medium tracking-tight mb-3">
                    {s.title}
                  </h3>
                  <p className="text-sm font-light forr-text-soft leading-relaxed">
                    {s.desc}
                  </p>
                  <div className="mt-8 inline-flex items-center gap-2 forr-text-teal-deep text-sm">
                    Read
                    <ArrowRight
                      size={14}
                      strokeWidth={1.75}
                      className="transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <NextStep
        overline="Start Here"
        title="Why Adaptive Physiology?"
        description="Traditional behavior change addresses thoughts, symptoms, or motivation. Adaptive Physiology begins somewhere different."
        to="/methodology/why"
        cta="Read the case"
        testId="methodology-next-step"
      />
    </PageShell>
  );
};

export default MethodologyOverview;
